import React from 'react';
import { useContext, useEffect, useState, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { withRouter } from 'react-router-dom';

import * as Yup from 'yup';
import i18n from 'i18n-js';
import { Link } from 'react-router-dom';
import { BastionContext } from 'context';
import { AuthLayout } from 'components/UI';
import { Formik, Form, Field } from 'formik';
import { Box, Button } from '@mui/material';
import MaterialLink from '@mui/material/Link';
import { AuthActions } from 'state/modules/auth';
import { TextField } from 'components/FormFields';
import { BastionsSelect } from 'components/AsyncFields';

const ForgotPasswordPage = () => {
  const dispatch = useDispatch();

  const { loading, errors, bastions } = useSelector((state) => state.auth);
  const [selectBastion, setSelectBastion] = useState(false);
  const [fetchedBastions, setFetchedBastion] = useState(false);
  const { theme, bastion } = useContext(BastionContext);
  const formRef = useRef(null);

  useEffect(() => {
    if (bastions && bastions.length && fetchedBastions) {
      if (bastions.length === 1) {
        formRef.current.setFieldValue('bastion_slug', bastions[0].id_slug);
        formRef.current.handleSubmit();
      }
      setSelectBastion(true);
      setShowFormInputs(!bastion);
    }
  }, [bastions, errors, fetchedBastions, bastion]);

  const initialValues = {
    email: '',
    bastion_slug: bastion || '',
  };

  const [showFormInputs, setShowFormInputs] = useState(!bastion && selectBastion);

  const ForgotPasswordSchema = Yup.object().shape({
    email: Yup.string()
      .email(i18n.t('common.invalid_email'))
      .required(i18n.t('common.required_field')),
    bastion_slug: Yup.string().when('$context', (context, bastionSchema) => {
      return bastion || showFormInputs
        ? bastionSchema.required(i18n.t('common.required_field'))
        : bastionSchema;
    }),
  });

  const submit = (values, { resetForm }) => {
    if (!bastion && !selectBastion) {
      setFetchedBastion(true);
      return dispatch(AuthActions.fetchBastions(values.email));
    }
    dispatch(AuthActions.forgotPassword(values));
    resetForm();
    dispatch(AuthActions.resetBastions(values.email));
    setFetchedBastion(false);
    setShowFormInputs(false);
    setSelectBastion(false);
  };

  return (
    <AuthLayout title={`${theme.page.title} | ${i18n.t('auth.reset_password')}`}>
      <Box style={{ paddingTop: '30%' }} height="100%">
        <Formik
          onSubmit={submit}
          innerRef={formRef}
          initialValues={initialValues}
          validationSchema={ForgotPasswordSchema}
        >
          {({ handleSubmit }) => (
            <Form noValidate onSubmit={handleSubmit}>
              <Box mb={8}>
                <Box fontSize={16} lineHeight={1} fontWeight="bold" color="primary.main">
                  {i18n.t('auth.welcome_to', { name: '' })}
                </Box>
                <Box mt={2} fontSize={32} lineHeight={1} fontWeight="bold">
                  {theme.page.title}
                </Box>
                <Box
                  mt={1}
                  fontSize={24}
                  lineHeight={1}
                  letterSpacing={6.6}
                  fontFamily="Montserrat"
                >
                  {theme.page.secondTitle}
                </Box>
              </Box>
              <Box mb={6} fontSize={16}>
                {i18n.t('auth.forgot_password_text')}
              </Box>
              <Box>
                <Box>
                  <Field
                    data-cy="email-input"
                    type="email"
                    name="email"
                    margin="none"
                    component={TextField}
                    label={i18n.t('fields.email')}
                  />
                </Box>
                {showFormInputs && bastions.length > 1 && (
                  <Box mt={1}>
                    <Field
                      name="bastion_slug"
                      component={BastionsSelect}
                      label={i18n.t('auth.select_network')}
                    />
                  </Box>
                )}
              </Box>
              <Box mt={6}>
                <Button
                  fullWidth
                  type="submit"
                  color="primary"
                  loading={loading}
                  disabled={loading}
                  variant="contained"
                  data-cy="reset-my-password"
                  sx={{ textTransform: 'none' }}
                >
                  {i18n.t('auth.reset_my_password')}
                </Button>
              </Box>
              <Box
                mt={4}
                display="flex"
                fontWeight="bold"
                color="primary.main"
                justifyContent="center"
              >
                <MaterialLink as="span" color="primary" underline="hover">
                  <Link to="login" style={{ color: 'inherit' }}>
                    {i18n.t('auth.back_to_login')}
                  </Link>
                </MaterialLink>
              </Box>
            </Form>
          )}
        </Formik>
      </Box>
    </AuthLayout>
  );
};

export default withRouter(ForgotPasswordPage);
